// src/pages/ClientBranchEditPage.jsx
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import parsePhoneNumber from "libphonenumber-js";
import "react-phone-number-input/style.css";
import { branchApi, locationApi } from "../Apis/Api";
import LoadingComponent from "../components/ui/LoadingComponent";
import Breadcrumb from "../components/Breadcrumb";
import CustomSelect from "../components/common/CustomSelect";
import CustomPhoneInput from "../components/common/CustomPhoneInput";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { handleApiResponse, handleCustomResponse } from "../utils/response";

// Unified phone validation function
const validatePhoneNumber = (value) => {
  if (!value) return false;
  try {
    const phoneNumber = parsePhoneNumber(value);
    return phoneNumber && phoneNumber.isValid() && phoneNumber.country;
  } catch {
    return false;
  }
};

// Status options - Updated
const statusOptions = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

// Validation schema with tax_ids and status - Updated
const validationSchema = Yup.object().shape({
  country_id: Yup.string().required("Country is required"),
  city_id: Yup.string().required("City is required"),
  branch_name: Yup.string()
    .required("Branch name is required")
    .min(2, "Branch name must be at least 2 characters")
    .max(100, "Branch name cannot exceed 100 characters")
    .matches(
      /^[a-zA-Z0-9\s\-&.()]+$/,
      "Branch name contains invalid characters"
    ),
  branch_code: Yup.string().required("Branch code is required"),
  address: Yup.string()
    .required("Address is required")
    .min(5, "Address must be at least 5 characters")
    .max(255, "Address cannot exceed 255 characters"),
  phone: Yup.string()
    .required("Phone number is required")
    .test(
      "is-valid-phone",
      "Please enter a valid international phone number",
      validatePhoneNumber
    )
    .test("min-length", "Phone number is too short", (value) => {
      if (!value) return false;
      try {
        const phoneNumber = parsePhoneNumber(value);
        return (
          phoneNumber &&
          phoneNumber.nationalNumber &&
          phoneNumber.nationalNumber.length >= 7
        );
      } catch {
        return false;
      }
    }),
  status: Yup.string().oneOf(["Active", "Inactive"]).required("Status is required"),
  tax_ids: Yup.array(),
});

// CustomSelect Component
const CustomSelectComponent = ({
  options,
  value,
  onChange,
  onInputChange,
  isLoading,
  isDisabled,
  placeholder,
  noOptionsMessage,
  errors,
  name,
  isSearchable = true,
  filterOption,
}) => {
  return (
    <CustomSelect
      name={name}
      options={options}
      value={value}
      onChange={onChange}
      onInputChange={onInputChange}
      isLoading={isLoading}
      isDisabled={isDisabled}
      placeholder={placeholder}
      noOptionsMessage={noOptionsMessage}
      isSearchable={isSearchable}
      filterOption={filterOption}
    />
  );
};

// FormField Component
const FormField = ({
  label,
  name,
  type,
  control,
  register,
  errors,
  options,
  isLoading,
  disabled,
  placeholder,
  onInputChange,
  defaultCountry,
  onChange,
  filterOption,
}) => {
  return (
    <div className="col-md-6 mb-20">
      <label className="form-label">{label} *</label>
      {type === "select" ? (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <CustomSelectComponent
              options={options}
              value={field.value}
              onChange={(selected) => {
                const newValue = selected ? selected.value : "";
                field.onChange(newValue);
                if (onChange) onChange(selected);
              }}
              onInputChange={onInputChange}
              isLoading={isLoading}
              isDisabled={disabled}
              placeholder={placeholder}
              noOptionsMessage={() => `No ${label.toLowerCase()} found`}
              errors={errors}
              name={name}
              filterOption={filterOption}
            />
          )}
        />
      ) : type === "phone" ? (
        <CustomPhoneInput
          name={name}
          control={control}
          errors={errors}
          defaultCountry={defaultCountry}
        />
      ) : (
        <input
          type={type}
          {...register(name)}
          className={`form-control radius-8 ${
            errors[name] ? "is-invalid" : ""
          }`}
          placeholder={placeholder}
          readOnly={type === "tel"}
        />
      )}
      {errors[name] && (
        <span className="text-danger-600">{errors[name].message}</span>
      )}
    </div>
  );
};

// Custom hook for edit form logic
const useClientBranchEditForm = (branchData) => {
  const navigate = useNavigate();
  const [allCountries, setAllCountries] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [isCountryLoading, setIsCountryLoading] = useState(false);
  const [isCityLoading, setIsCityLoading] = useState(false);
  const [isTaxLoading, setIsTaxLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState("PK");
  const [submit, setSubmit] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      country_id: "",
      city_id: "",
      branch_name: "",
      branch_code: "",
      address: "",
      phone: "",
      status: "Active",
      tax_ids: [],
    },
  });

  const countryId = watch("country_id");

  // Fetch all countries
  const fetchCountries = useCallback(async () => {
    setIsCountryLoading(true);
    try {
      const response = await locationApi.getAllCountries({
        want_pagination: 0,
        sort_by: "country_name",
        sort_order: "asc",
      });
      const responseData = response.data || [];
      setAllCountries(responseData);
      handleApiResponse(response, "load countries", "GET");
    } catch (error) {
      handleApiResponse(error, "Failed to load countries", "GET");
    } finally {
      setIsCountryLoading(false);
    }
  }, []);

  // Fetch all cities
  const fetchCities = useCallback(async (countryIdParam) => {
    if (!countryIdParam) {
      setAllCities([]);
      return;
    }
    setIsCityLoading(true);
    try {
      const response = await locationApi.getAllCities({
        country_id: Number(countryIdParam),
        want_pagination: 0,
        sort_by: "city_name",
        sort_order: "asc",
      });
      const citiesData = response.data || [];
      setAllCities(citiesData);
      handleApiResponse(response, "load cities", "GET");
    } catch (error) {
      handleApiResponse(error, "Failed to load cities", "GET");
      setAllCities([]);
    } finally {
      setIsCityLoading(false);
    }
  }, []);

  // Fetch taxes by country
  const fetchTaxes = useCallback(async (countryId) => {
    try {
      setIsTaxLoading(true);
      const res = await locationApi.getTaxByCountry({
        country_id: Number(countryId),
      });
      setTaxes(res.data || []);
      handleApiResponse(res, "load taxes", "GET");
    } catch (error) {
      handleApiResponse(error, "Failed to load taxes", "GET");
    } finally {
      setIsTaxLoading(false);
    }
  }, []);

  // Handle country change
  const handleCountryChange = useCallback(
    (selected) => {
      const newCountryId = selected ? String(selected.value) : "";
      setValue("country_id", newCountryId, { shouldValidate: true });
      setValue("city_id", "", { shouldValidate: true });
      setValue("tax_ids", [], { shouldValidate: true });
      setAllCities([]);
      setTaxes([]);
      setCitySearchTerm("");
      const selectedCountry = allCountries.find(
        (c) => String(c.country_id) === newCountryId
      );
      setSelectedCountryCode(
        selectedCountry && selectedCountry.country_code
          ? selectedCountry.country_code
          : "PK"
      );
      if (newCountryId) {
        fetchCities(newCountryId);
        fetchTaxes(newCountryId);
      }
    },
    [setValue, fetchCities, fetchTaxes, allCountries]
  );

  // Handle search input changes
  const handleCountrySearch = (inputValue) => {
    setCountrySearchTerm(inputValue);
  };

  const handleCitySearch = (inputValue) => {
    setCitySearchTerm(inputValue);
  };

  // Custom filter functions
  const countryFilterOption = useCallback((option, inputValue) => {
    if (!inputValue) return true;
    return option.label.toLowerCase().includes(inputValue.toLowerCase());
  }, []);

  const cityFilterOption = useCallback((option, inputValue) => {
    if (!inputValue) return true;
    return option.label.toLowerCase().includes(inputValue.toLowerCase());
  }, []);

  // Memoized options
  const countryOptions = useMemo(() => {
    return allCountries.map((c) => ({
      value: String(c.country_id),
      label: c.country_name,
    }));
  }, [allCountries]);

  const cityOptions = useMemo(() => {
    return allCities.map((c) => ({
      value: String(c.city_id),
      label: c.city_name,
    }));
  }, [allCities]);

  // Submit function - Updated
  const onSubmit = async (data) => {
    setSubmit(true);
    try {
      if (!validatePhoneNumber(data.phone)) {
        handleCustomResponse("error", "Please enter a valid phone number");
        return;
      }

      const phoneNumber = parsePhoneNumber(data.phone);
      const payload = {
        country_id: parseInt(data.country_id),
        city_id: parseInt(data.city_id),
        branch_name: data.branch_name,
        branch_code: data.branch_code,
        address: data.address,
        phone_number: phoneNumber.format("E.164"),
        // ✅ Status ko properly handle karein
        is_active: data.status === "Active",
        tax_ids: data.tax_ids.length > 0 ? data.tax_ids.map(Number) : [],
      };

      const response = await branchApi.updateClientBranch(
        branchData.id,
        payload
      );
      handleApiResponse(response, "Branch updated successfully", "PUT");

      navigate("/branch-list");
    } catch (error) {
      handleApiResponse(error, "Failed to update branch", "PUT");
    } finally {
      setSubmit(false);
    }
  };

  // Initial data loading and form reset - Updated
  useEffect(() => {
    if (!branchData) {
      handleCustomResponse("error", "No branch data provided");
      setLoading(false);
      return;
    }

    console.log("Branch Data from List:", branchData);
    console.log("Branch Status:", branchData.is_active);
    console.log("Branch Status Type:", typeof branchData.is_active);

    const initializeForm = async () => {
      try {
        // Fetch initial data
        await fetchCountries();

        // Set form values - Status ko properly convert karein
        const statusValue = branchData.is_active !== undefined ? 
          (branchData.is_active === true || branchData.is_active === "Active" ? "Active" : "Inactive")
          : "Active";
        
        console.log("Converted Status Value:", statusValue);

        reset({
          country_id: branchData.country_id
            ? String(branchData.country_id)
            : "",
          city_id: branchData.city_id ? String(branchData.city_id) : "",
          branch_name: branchData.branch_name || "",
          branch_code: branchData.branch_code || "",
          address: branchData.address || "",
          phone: branchData.phone_number || "",
          status: statusValue,
          tax_ids: branchData.tax_id ? branchData.tax_id.map(Number) : [],
        });

        // Country set hone ke baad cities aur taxes fetch karein
        if (branchData.country_id) {
          await fetchCities(String(branchData.country_id));
          await fetchTaxes(String(branchData.country_id));
          
          // Country code set karein
          const selectedCountry = allCountries.find(
            (c) => String(c.country_id) === String(branchData.country_id)
          );
          if (selectedCountry && selectedCountry.country_code) {
            setSelectedCountryCode(selectedCountry.country_code);
          }
        }
      } catch (error) {
        handleCustomResponse("error", "Error initializing form");
      } finally {
        setLoading(false);
      }
    };

    initializeForm();
  }, [branchData, reset, fetchCountries, fetchCities]);

  return {
    control,
    handleSubmit,
    setValue,
    watch,
    register,
    errors,
    countryOptions,
    cityOptions,
    taxes,
    isCountryLoading,
    isCityLoading,
    isTaxLoading,
    loading,
    handleCountryChange,
    onSubmit,
    handleCountrySearch,
    handleCitySearch,
    countryFilterOption,
    cityFilterOption,
    selectedCountryCode,
    submit,
  };
};

// BranchForm component
const BranchForm = ({ formProps, isEdit = false }) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    errors,
    isCountryLoading,
    isCityLoading,
    isTaxLoading,
    countryOptions,
    cityOptions,
    taxes,
    handleCountryChange,
    onSubmit,
    handleCountrySearch,
    handleCitySearch,
    countryFilterOption,
    cityFilterOption,
    selectedCountryCode,
    submit,
  } = formProps;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="row g-20">
        <FormField
          label="Country"
          name="country_id"
          type="select"
          control={control}
          errors={errors}
          options={countryOptions}
          isLoading={isCountryLoading}
          placeholder="Search or Select Country"
          onChange={handleCountryChange}
          onInputChange={handleCountrySearch}
          filterOption={countryFilterOption}
        />
        <FormField
          label="City"
          name="city_id"
          type="select"
          control={control}
          errors={errors}
          options={cityOptions}
          isLoading={isCityLoading}
          disabled={!watch("country_id")}
          placeholder="Search or Select City"
          onInputChange={handleCitySearch}
          filterOption={cityFilterOption}
        />
        <FormField
          label="Branch Name"
          name="branch_name"
          type="text"
          register={register}
          errors={errors}
          placeholder="Enter Branch Name"
        />
        <div className="col-md-6 mb-20">
          <CustomPhoneInput
            name="phone"
            control={control}
            errors={errors}
            defaultCountry={selectedCountryCode}
          />
        </div>
        <FormField
          label="Address"
          name="address"
          type="text"
          register={register}
          errors={errors}
          placeholder="Enter Address"
        />
        <FormField
          label="Branch Code"
          name="branch_code"
          type="tel"
          register={register}
          errors={errors}
          placeholder="Enter Branch Code"
        />
        
        {/* Status Field - Updated */}
        <div className="col-md-6 mb-20">
          <label className="form-label">Status *</label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => {
              return (
                <CustomSelect
                  name="status"
                  value={statusOptions.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(option) =>
                    field.onChange(option ? option.value : null)
                  }
                  options={statusOptions}
                  placeholder="Select Status"
                  noOptionsMessage={() => (
                    <div>
                      <p>No status options available</p>
                    </div>
                  )}
                />
              );
            }}
          />
          {errors.status && (
            <span className="text-danger-600">{errors.status.message}</span>
          )}
        </div>

        {/* Taxes Field */}
        <div className="col-md-6 mb-20">
          <label className="form-label">Taxes *</label>
          <div className="d-flex flex-column gap-2">
            {isTaxLoading ? (
              <span>Loading taxes...</span>
            ) : taxes.length > 0 ? (
              taxes.map((tax) => (
                <label
                  key={tax.tax_id}
                  className="d-flex align-items-center gap-2"
                >
                  <Controller
                    name="tax_ids"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        value={tax.tax_id}
                        checked={field.value?.includes(Number(tax.tax_id))}
                        onChange={(e) => {
                          const currentValues = field.value || [];
                          let updatedValues;
                          if (e.target.checked) {
                            updatedValues = [
                              ...currentValues,
                              Number(e.target.value),
                            ];
                          } else {
                            updatedValues = currentValues.filter(
                              (id) => id !== Number(e.target.value)
                            );
                          }
                          field.onChange(
                            updatedValues.length > 0 ? updatedValues : []
                          );
                        }}
                        disabled={!watch("country_id")}
                        className="form-check-input"
                      />
                    )}
                  />
                  {`${tax.tax_type_name} (${tax.tax_percent}%)`}
                </label>
              ))
            ) : (
              <span>No taxes available</span>
            )}
          </div>
          {errors.tax_ids && (
            <span className="text-danger-600">{errors.tax_ids.message}</span>
          )}
        </div>
      </div>
      <div className="d-flex justify-content-center gap-3">
        <CancelButtonForm />
        <UpdateButtonForm disabled={submit} />
      </div>
    </form>
  );
};

// Main component
const ClientBranchEditPage = () => {
  const { state } = useLocation();
  const { branchData } = state || {};
  const formProps = useClientBranchEditForm(branchData);

  return formProps.loading ? (
    <>
      <Breadcrumb title="Edit Branch" />
      <LoadingComponent />
    </>
  ) : (
    <>
      <Breadcrumb title="Edit Branch" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <BranchForm formProps={formProps} isEdit={true} />
        </div>
      </div>
    </>
  );
};

export default ClientBranchEditPage;